'use strict';

import {BaseElement} from "../../../../interfaces/BaseElement";
import Hammer from "hammerjs";
import { clamp, freezingCanvas, isPortraitMode, UIFreezing, zoomOut, zoomToApartment } from '../../../../../functions';
import {EventBus} from "../../../../interfaces/EventBus";
import {changeLangToCurrent} from "../../../../components/langSwitcher/langSwitcher";
import {Buildings, isNoMenu, projectMode, settingsData, threeJsVariables, UIRoot} from '../../../../../settings';
import {ViewMode} from "../../../../../common";
import { updateComponentsOnCanvasResize } from '../../search';
import {IntersectionLegenda} from "../../../../components/IntersectionLegenda";

export class SearchBottomElement {
	#rootElement;
	#dragStarted;
	#panDirection;
	#currentHeight;

	constructor() {
		this.#rootElement = new BaseElement()
			.setAttributes({'data-state': 'hidden'})
			.addClass('mobile-search__bottom-element');

		if (isNoMenu)
			this.#rootElement.addClass('no-menu-mode');

		this.#addListeners();

		EventBus.add('searchBottomElementFold', this.#fold.bind(this));
		EventBus.add('searchBottomElementUnfold', this.#unfold.bind(this));
	}

	get rootElement() {
		return this.#rootElement;
	}

	remove() {
		EventBus.remove('searchBottomElementFold');
		EventBus.remove('searchBottomElementUnfold');

		this.#rootElement.remove();
		this.#rootElement = null;
	}

	#addListeners() {
		this.#dragStarted = false;
		this.#panDirection = null;
		this.#currentHeight = null;
		
		this.#rootElement.event('click',this.#clickHandler.bind(this));

		const swipe = new Hammer.Manager(this.#rootElement.DOMElement);
		swipe.add(new Hammer.Pan({
			threshold: 10
		}));

		swipe.on('panstart',(e) => {
			if (e.target.closest('.mobile-search__results-viewer') && this.#rootElement.DOMElement.getAttribute('data-state') === 'opened-fully')
				return;

			if (e.additionalEvent === 'panup') {
				this.#rootElement.DOMElement.querySelector('.mobile-search__results-viewer').classList.add('invisible');
				freezingCanvas(true);
			}
		});

		swipe.on('panup pandown', (e) => {
			if (e.target.closest('.mobile-search__results-viewer') && this.#rootElement.DOMElement.getAttribute('data-state') === 'opened-fully')
				return;

			if (!this.#currentHeight)
				this.#currentHeight = this.#rootElement.DOMElement.getBoundingClientRect().height;

			this.#panDirection = e.additionalEvent;

			if (isPortraitMode()) {
				const min = this.#rootElement.DOMElement.getAttribute('data-state') === 'focused' ? 120 : 75;
				const height = clamp(this.#currentHeight - e.deltaY, min, window.innerHeight - 70);
				this.#rootElement
					.addStyle('height', height + 'px')
					.addStyle('transition', 'none');
				this.#dragStarted = true;

				if (e.isFinal) {
					this.#panHandler();
				}

			}
		});

		swipe.on('panend', (e) => {
			if (e.target.closest('.mobile-search__results-viewer') && this.#rootElement.DOMElement.getAttribute('data-state') === 'opened-fully')
				return;

			if (this.#dragStarted && this.#panDirection)
				this.#panHandler();
		});
	}

	#panHandler(direction) {
		this.#rootElement.removeStyle('transition');
		direction = direction || this.#panDirection;
		switch (direction) {
			case 'panup':
				this.#swipeUpHandler();
				break;
			case 'pandown':
				this.#swipedDownHandler();
				break;
		}

		this.#rootElement.removeStyle('height');
		this.#dragStarted = false;
		this.#panDirection = null;
		this.#currentHeight = null;
		this.#rootElement.event('transitionend', () => {
			this.#rootElement.DOMElement.querySelector('.mobile-search__results-viewer').classList.remove('invisible');

			const state = this.#rootElement.DOMElement.getAttribute('data-state');
			if (state !== 'opened-fully') {
				freezingCanvas(false);
				EventBus.trigger('resultsViewerScrollSlideIntoView');
			}

			if (state === 'focused') {
				zoomToApartment(settingsData.lastClickedFlat);
			} else if (state === 'hidden') {
				zoomOut();
			}

			if (state === 'hidden') {
				IntersectionLegenda.show();
				EventBus.trigger('buildingSwitcherShow');
			} else {
				IntersectionLegenda.hide();
				EventBus.trigger('buildingSwitcherHide');
			}
		}, {once: true});
	};

	async #swipeUpHandler() {
		const state = this.#rootElement.DOMElement.getAttribute('data-state');
		switch(state) {
			case "focused":
				this.#rootElement.setAttributes({'data-state': 'opened-fully'});
				const activeSlide = this.#rootElement.DOMElement.querySelector('.active-slide');
				if (activeSlide)
					activeSlide.scrollIntoView({behavior: "instant", inline: "center"});
				break;
			case "minimized":
			case "hidden":
				this.#rootElement.setAttributes({'data-state': 'focused'});
				break;
		}
	}

	#swipedDownHandler() {
		const state = this.#rootElement.DOMElement.getAttribute('data-state');
		switch(state) {
			case "minimized":
			case "focused":
				this.#rootElement.setAttributes({'data-state': 'hidden'});
				break;
			case "opened-fully":
				this.#rootElement.DOMElement.querySelector('.mobile-search__results-viewer').classList.add('invisible');
				this.#rootElement.setAttributes({'data-state': 'focused'});
				break;
		}
	}
	
	#clickHandler({target}) {
		if (
			target.classList.contains('mobile-search__bottom-element') ||
			target.classList.contains('building-switcher__info-element') ||
			target.classList.contains('building-switcher__building-name') ||
			target.classList.contains('mobile-search__results-amount') ||
			target.classList.contains('results-amount__counter') ||
			target.classList.contains('results-amount__text')
		) {
			const state = this.#rootElement.DOMElement.getAttribute('data-state');
			if (state === 'hidden') {
				this.#unfold();
			}
		}
		
	}

	#fold() {
		if (!this.#rootElement || projectMode.LiteMode.modeIs('empty'))
			return;
		if (this.#rootElement.getAttribute('data-state') === 'hidden')
			return;
		if (settingsData.activeViewMode !== ViewMode.FloorPlan && !projectMode.LiteMode.modeIs('picture'))
			zoomOut();

		IntersectionLegenda.show();
		EventBus.trigger('buildingSwitcherShow');

		this.#rootElement.setAttributes({'data-state': 'hidden'});
	}

	#unfold() {
		if (!this.#rootElement)
			return;
		const state = this.#rootElement.getAttribute('data-state');
		if (state !== 'opened-fully' && state === 'hidden')
			this.#rootElement.setAttributes({'data-state': 'focused'});
		if (!settingsData.lastClickedFlat)
			return;

		IntersectionLegenda.hide();
		EventBus.trigger('buildingSwitcherHide');

		zoomToApartment(settingsData.lastClickedFlat)
	}
}
